/**
 * Permission-based access control utilities
 * Best Practice: Routes should be protected by permissions, not roles directly
 * All users, including SuperAdmin, must have the required permissions to access routes
 */

// Permission constants - actual permission names from JWT token
export const PERMISSIONS = {
  // Tenant management permissions
  CREATE_TENANT: 'internal_create_tenant',
  EDIT_TENANT: 'internal_edit_tenant',
  EDIT_TENANT_MODULES: 'internal_edit_tenant_modules',
  DELETE_TENANT: 'internal_delete_tenant',
  
  // User management permissions
  ADD_USER: 'internal_add_user',
  EDIT_USER: 'internal_edit_user',
  DELETE_USER: 'internal_delete_user',
  
  // Role management permissions
  CREATE_ROLE: 'internal_create_role',
  EDIT_ROLE: 'internal_edit_role',
  DELETE_ROLE: 'internal_delete_role',
  CHANGE_PERMISSIONS_ASSOCIATION: 'internal_change_permissions_association',
  EDIT_ROLE_TENANT_ASSOCIATION: 'internal_edit_role_tenant_association',
  
  // Permission management permissions
  CREATE_PERMISSION: 'internal_create_permission',
  EDIT_PERMISSION: 'internal_edit_permission',
  DELETE_PERMISSION: 'internal_delete_permission',
  CHANGE_ROLE_ASSOCIATION: 'internal_change_role_association',
  
  // Subscription management permissions
  CREATE_SUBSCRIPTION: 'internal_create_subscription',
  EDIT_SUBSCRIPTION: 'internal_edit_subscription',
  DISABLE_SUBSCRIPTION: 'internal_disable_subscription',
  EDIT_SUBSCRIPTION_MODULES: 'internal_edit_subscription_modules',
  DELETE_SUBSCRIPTION: 'internal_delete_subscription',
  VIEW_SUBSCRIPTION: 'internal_view_subscription',
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

/**
 * Get all available permissions in the system
 * Used to check if user has at least one permission to access the platform
 */
export const getAllPermissions = (): string[] => {
  return Object.values(PERMISSIONS);
};

/**
 * Route to permission mapping
 * Each route requires ANY of the listed permissions (OR logic)
 * User needs at least one permission from the array to access the route
 */
export const ROUTE_PERMISSIONS: Record<string, string[]> = {
  // Dashboard - accessible to all authenticated users (no specific permission required)
  '/': [],
  
  // Users route - requires any user management permission
  '/users': [
    PERMISSIONS.ADD_USER,
    PERMISSIONS.EDIT_USER,
    PERMISSIONS.DELETE_USER,
  ],
  
  // Tenants route - requires any tenant management permission
  '/tenants': [
    PERMISSIONS.CREATE_TENANT,
    PERMISSIONS.EDIT_TENANT,
    PERMISSIONS.EDIT_TENANT_MODULES,
    PERMISSIONS.DELETE_TENANT,
  ],
  
  // Roles route - requires any role management permission
  '/roles': [
    PERMISSIONS.CREATE_ROLE,
    PERMISSIONS.EDIT_ROLE,
    PERMISSIONS.DELETE_ROLE,
    PERMISSIONS.CHANGE_PERMISSIONS_ASSOCIATION,
  ],
  
  // Permissions route - requires any permission management permission
  '/permissions': [
    PERMISSIONS.CREATE_PERMISSION,
    PERMISSIONS.EDIT_PERMISSION,
    PERMISSIONS.DELETE_PERMISSION,
    PERMISSIONS.CHANGE_ROLE_ASSOCIATION,
  ],
  
  // Subscriptions route - requires any subscription management permission
  '/subscriptions': [
    PERMISSIONS.CREATE_SUBSCRIPTION,
    PERMISSIONS.EDIT_SUBSCRIPTION,
    PERMISSIONS.DISABLE_SUBSCRIPTION,
    PERMISSIONS.EDIT_SUBSCRIPTION_MODULES,
    PERMISSIONS.DELETE_SUBSCRIPTION,
    PERMISSIONS.VIEW_SUBSCRIPTION,
  ],
  
  // Reporting and KPIs - accessible to all authenticated users (no specific permission required)
  '/reporting': [],
  '/kpis': [],
};

/**
 * Check if user has at least one permission to access the platform
 * @param userPermissions - Array of permission names/IDs that the user has (from JWT token)
 * @returns true if user has at least one permission
 */
export const hasAnyPermission = (userPermissions: string[]): boolean => {
  if (!userPermissions || userPermissions.length === 0) {
    return false;
  }

  const allSystemPermissions = getAllPermissions();
  const hasPermission = userPermissions.some(userPerm => 
    allSystemPermissions.some(sysPerm => 
      userPerm.toLowerCase() === sysPerm.toLowerCase() ||
      userPerm === sysPerm
    )
  );

  return hasPermission;
};

/**
 * Check if user has required permissions for a route
 * @param userRoleId - The role ID of the current user (for logging purposes)
 * @param userPermissions - Array of permission names/IDs that the user has (from JWT token)
 * @param requiredPermissions - Array of permission names required for the route
 * @returns true if user has at least one required permission (OR logic)
 * Note: All users, including SuperAdmin, must have the required permissions
 */
export const hasPermissionAccess = (
  userRoleId: string | null | undefined,
  userPermissions: string[],
  requiredPermissions: string[]
): boolean => {
  // If no user role ID, deny access
  if (!userRoleId) {
    console.log('Access denied: No user role ID found', { userRoleId });
    return false;
  }

  // Check if user has at least one permission to access the platform
  if (!hasAnyPermission(userPermissions)) {
    console.log('Access denied: User has no permissions', { 
      userRoleId,
      userPermissions 
    });
    return false;
  }

  // If route has no required permissions, allow access (e.g., Dashboard, Reporting)
  // But only if user has at least one permission (checked above)
  if (requiredPermissions.length === 0) {
    console.log('Access granted: Route has no required permissions', { 
      pathname: 'current route',
      userRoleId 
    });
    return true;
  }

  // Check if user has ANY of the required permissions (OR logic)
  // User needs at least one permission from the requiredPermissions array
  // All users, including SuperAdmin, must have the required permissions
  const hasRequiredPermission = requiredPermissions.some(requiredPerm => 
    userPermissions.some(userPerm => 
      userPerm.toLowerCase() === requiredPerm.toLowerCase() ||
      userPerm === requiredPerm // Also check exact match
    )
  );

  console.log('Permission access check:', {
    userRoleId,
    userPermissions,
    requiredPermissions,
    hasRequiredPermission
  });

  return hasRequiredPermission;
};

/**
 * Get required permissions for a specific route
 * @param pathname - The route pathname
 * @returns Array of permission names required for this route
 */
export const getRequiredPermissionsForRoute = (pathname: string): string[] => {
  // Normalize pathname (remove trailing slash, handle exact matches)
  const normalizedPath = pathname === '/' ? '/' : pathname.replace(/\/$/, '');
  
  return ROUTE_PERMISSIONS[normalizedPath] || [];
};
